import React, { useState, useEffect } from 'react';
import { useAppContext } from './AppContext';
import ProposalsTab2 from './ProposalsTab2';
import PurchaseOrdersTab from './PurchaseOrdersTab';
import ReceiptsTab from './ReceiptsTab';
import InvoicesTab from './InvoicesTab';
import OwnerDrawsSection from './OwnerDrawsSection';
import FirmCreditCardsSection from './FirmCreditCardsSection';
import ProjectNotesTab from './ProjectNotesTab';
import BillingTab from './BillingTab';
import DataProtection from './DataProtection';
import FullExportPanel from './FullExportPanel';
import DocumentsTab from './DocumentsTab';
import QuotesTab from './QuotesTab';
import SchedulesTab from './SchedulesTab';
import FeatureRequests from './FeatureRequests';
import { hasAdminAccess } from '../utils';
import TrackerView from './TrackerView';
import CalendarView from './CalendarView';
import MessagesTab from './MessagesTab';
import VendorDirectory from './VendorDirectory';
import ItemDatabase from './ItemDatabase';
import { FirmSettings, ClientDirectory, ProjectDirectory } from './adminTabs';
import ReconciliationSection from './ReconciliationSection';
import Ledger from './Ledger/Ledger';
import { ProposalsAndPOs, generateFullCSV, downloadCSV, ImageExportPanel } from './documentTabs';

function App({onSignOut, currentPath, onNavigate}){
  // ── Tab routing ──────────────────────────────────────────────────────────
  var TAB_SLUGS={
    0:'items',1:'tracker',2:'proposals-pos',3:'in-out',
    4:'vendors',6:'requests',7:'projects',
    8:'billing',9:'data',10:'clients',11:'settings',
    12:'samples',13:'quotes',14:'memos',15:'proposals',
    16:'purchase-orders',17:'schedules',18:'project-notes',
    19:'receipts',20:'invoices',22:'owner-draws',
    23:'payment-methods',24:'reconciliation',25:'ledger',
    26:'calendar',27:'messages'
  };
  var SLUG_TO_TAB={};
  Object.keys(TAB_SLUGS).forEach(function(k){SLUG_TO_TAB[TAB_SLUGS[k]]=parseInt(k);});

  function tabFromPath(pathname){
    var slug=(pathname||'/').replace(/^\//, '').toLowerCase().split('/')[0]||'items';
    return SLUG_TO_TAB[slug]!==undefined?SLUG_TO_TAB[slug]:0;
  }
  function itemIdFromPath(pathname){
    var parts=(pathname||'').replace(/^\//, '').split('/');
    return (parts[0]==='items'&&parts[1])?parts[1]:null;
  }

  var activePath=currentPath||'/items';
  var ts=useState(function(){return tabFromPath(activePath);});
  var tab=ts[0];var setTab=ts[1];

  React.useEffect(function(){
    setTab(tabFromPath(activePath));
  },[activePath]);

  // ── All data comes from AppContext ───────────────────────────────────────
  var ctx=useAppContext();
  var items=ctx.items;
  var vendors=ctx.vendors;
  var projects=ctx.projects;
  var clients=ctx.clients;
  var firmSettings=ctx.firmSettings;
  var proposals=ctx.proposals;
  var setProposals=ctx.setProposals;
  var vendorPayments=ctx.vendorPayments;
  var setVendorPayments=ctx.setVendorPayments;
  var userEmail=ctx.userEmail;
  var isOwner=ctx.isOwner;
  var ready=ctx.ready;
  var saveError=ctx.saveError;
  var savedI=ctx.savedI;
  var savedV=ctx.savedV;
  var savedP=ctx.savedP;
  var savedC=ctx.savedC;
  var savedFS=ctx.savedFS;
  var setVendors=ctx.setVendors;
  var setProjects=ctx.setProjects;
  var setClients=ctx.setClients;
  var setFirmSettings=ctx.setFirmSettings;
  var setItems=ctx.setItems;
  var addItem=ctx.addItem;
  var updateItem=ctx.updateItem;
  var deleteItem=ctx.deleteItem;
  var addVendorInline=ctx.addVendorInline;
  var addItems=ctx.addItems;
  var restoreData=ctx.restoreData;
  var queueSave=ctx.queueSave;
  var flushSaves=ctx.flushSaves;
  var saveItemDirect=ctx.saveItemDirect;
  var assemblies=ctx.assemblies||[];
  var receipts=ctx.receipts||[];

  // Approval-queue count for the Receipts nav badge. RLS already hides
  // receipts the current viewer can't see, so we just count pending +
  // needs_link from the local list.
  var pendingReceiptCount=receipts.filter(function(r){
    return r&&(r.status==='pending'||r.status==='needs_link');
  }).length;
  var addAssembly=ctx.addAssembly;
  var updateAssembly=ctx.updateAssembly;
  var deleteAssembly=ctx.deleteAssembly;
  var deleteItemDirect=ctx.deleteItemDirect;

  if(!ready)return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"60vh",flexDirection:"column",gap:12,color:"#888"}}>
      <div style={{fontSize:22}}></div>
      <div style={{fontSize:14}}>Loading your data…</div>
    </div>
  );

  var ownerEmail="c@cimonstarr.com";
  // Path B (added 2026-05-07): bookkeeper email is now configurable in
  // Firm Settings. Falls back to the historical default when unset so
  // existing deployments don't break. The defensive normalization
  // strips whitespace + leading/trailing quote chars to harden against
  // the JSON-stringified-email pattern that confused diagnostics on
  // 2026-05-06 (turned out to be a phantom; kept the normalization
  // anyway as belt-and-suspenders).
  function normalizeEmail(s) {
    if (!s) return '';
    return String(s).trim().replace(/^["']+|["']+$/g, '').toLowerCase();
  }
  var normalizedUserEmail = normalizeEmail(userEmail);
  var isOwnerNav = !!(normalizedUserEmail && normalizedUserEmail === ownerEmail);
  // Finance gate: owner OR any team member with hasAdminAccess. Drives
  // visibility of financeOnly tabs (Client Invoices, Finance, Reconciliation, Ledger)
  // in NAV_GROUPS via the financeOnly flag check below. The single source
  // of truth for "non-owner admin" is hasAdminAccess() in utils.js.
  var isFinanceNav = isOwnerNav || hasAdminAccess(userEmail, firmSettings);

  function navTo(i){
    if(window._hasUnsavedChanges&&!window.confirm("You have unsaved changes. Leave without saving?")){return;}
    window._hasUnsavedChanges=false;
    if(window._resetItemDatabase)window._resetItemDatabase();
    var slug=TAB_SLUGS[i]||'items';
    if(onNavigate){
      onNavigate('/'+slug); // triggers location.pathname change → useEffect → setTab
    } else {
      setTab(i); // fallback for any context without a router
    }
  }

  var NAV_GROUPS=[
    {label:"Workspace",items:[
      {label:"Items",tab:0,icon:"▤"},
      {label:"Tracker",tab:1,icon:"◎"},
      {label:"Calendar",tab:26,icon:"◫"},
    ]},
    {label:"Procurement",items:[
      {label:"Samples",tab:12,icon:"◇"},
      {label:"Quotes",tab:13,icon:"◻"},
      {label:"Memos",tab:14,icon:"◈"},
      {label:"Proposals",tab:15,icon:"◎"},
      {label:"Purchase Orders",tab:16,icon:"⊡"},
      {label:"Receipts",tab:19,icon:"◐"},
      {label:"Messages",tab:27,icon:"✉"},
    ]},
    {label:"Studio",items:[
      {label:"Project Notes",tab:18,icon:"◈"},
    ]},
    {label:"Directory",items:[
      {label:"Vendors",tab:4,icon:"◈"},
    ]},
    {label:"Tools",items:[
      {label:"Schedules",tab:17,icon:"▦"},
      {label:"Import",tab:3,icon:"⊕"},
      {label:"Requests",tab:6,icon:"⊡"},
    ]},
    {label:"Admin",items:[
      // Bookkeeper-visible (financeOnly: owner OR bookkeeper)
      {label:"Client Invoices",tab:20,icon:"⊞",financeOnly:true},
      {label:"Finance",tab:8,icon:"$",financeOnly:true},
      {label:"Reconciliation",tab:24,icon:"R",financeOnly:true},
      {label:"Ledger",tab:25,icon:"≡",financeOnly:true},
      {label:"Data",tab:9,icon:"⛁",ownerOnly:true},
    ]},
    {label:"Firm",items:[
      {label:"Owner",tab:22,icon:"◇",financeOnly:true},
      {label:"Payment Methods",tab:23,icon:"⊟",ownerOnly:true},
      {label:"Firm Settings",tab:11,icon:"⚙",ownerOnly:true},
      {label:"Clients",tab:10,icon:"◎",ownerOnly:true},
      {label:"Projects",tab:7,icon:"⌂",ownerOnly:true},
    ]},
  ];

  var sidebar=(
    <div style={{width:196,flexShrink:0,borderRight:"1px solid #ebebeb",display:"flex",flexDirection:"column",background:"#fafafa",minHeight:"100vh"}}>
      <div style={{padding:"20px 16px 12px"}}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:2,color:"#bbb",textTransform:"uppercase",marginBottom:2}}>Cimon Starr</div>
        <div style={{fontSize:14,fontWeight:700,color:"#1a1a1a",letterSpacing:-.3}}>Procurement</div>
      </div>
      <div style={{flex:1,overflowY:"auto",paddingBottom:16}}>
        {NAV_GROUPS.map(function(group,gi){
          if(group.ownerOnly&&!isOwnerNav){return null;}
          if(group.financeOnly&&!isFinanceNav){return null;}
          var visibleItems=group.items.filter(function(item){
            if(item.ownerOnly&&!isOwnerNav)return false;
            if(item.financeOnly&&!isFinanceNav)return false;
            return true;
          });
          if(!visibleItems.length){return null;}
          return (
            <div key={gi} style={{marginBottom:4}}>
              <div style={{fontSize:9,fontWeight:700,letterSpacing:1.5,color:"#bbb",textTransform:"uppercase",padding:"10px 16px 4px"}}>{group.label}</div>
              {visibleItems.map(function(item){
                var active=tab===item.tab;
                // Per-item badge count. Today only Receipts has one; if
                // we add more (e.g., Change Orders pending) wire them
                // through the same map.
                var badge=item.tab===19?pendingReceiptCount:0;
                return (
                  <button key={item.label} onClick={function(){navTo(item.tab);}} style={{
                    display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 16px",border:"none",background:active?"#ededff":"none",
                    color:active?"#3730a3":"#444",fontWeight:active?600:400,
                    fontSize:13,cursor:"pointer",textAlign:"left",borderRadius:0,
                    borderLeft:active?"3px solid #3730a3":"3px solid transparent",
                    transition:"background .1s",
                  }}>
                    <span style={{fontSize:13,opacity:active?1:0.5}}>{item.icon}</span>
                    <span style={{flex:1}}>{item.label}</span>
                    {badge>0&&(
                      <span style={{
                        background:"#D4007A",color:"#fff",
                        fontSize:10,fontWeight:600,
                        padding:"1px 6px",borderRadius:8,
                        minWidth:16,textAlign:"center",
                      }}>{badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
      {onSignOut&&(
        <div style={{padding:"12px 16px",borderTop:"1px solid #ebebeb"}}>
          <button onClick={onSignOut} style={{width:"100%",padding:"7px 10px",background:"none",border:"1px solid #ddd",borderRadius:6,fontSize:11,color:"#888",cursor:"pointer",textAlign:"center"}}>Sign out</button>
        </div>
      )}
    </div>
  );

  return (
    <div style={{display:"flex",minHeight:"100vh",color:"#1a1a1a"}}>
      {sidebar}
      <div style={{flex:1,minWidth:0,padding:"24px 28px",overflowY:"auto"}}>
        {/* Save error banner — surfaces window._setSaveError messages.
            Earlier this was wired in AppContext but never rendered, so save
            failures (RLS rejections, schema drift, etc.) were silent and
            optimistic UI lied about persistence. Always visible at top of
            content area when set. */}
        {saveError && (
          <div style={{
            marginBottom: 16, padding: '10px 14px',
            background: '#fff', border: '0.5px solid #C8322A',
            borderRadius: 4, color: '#C8322A',
            fontSize: 12, fontFamily: "'DM Sans', sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span>{saveError}</span>
            <button onClick={function(){ if(window._setSaveError) window._setSaveError(''); }}
              style={{ background:'transparent', border:'none', color:'#C8322A', cursor:'pointer', fontSize:16, padding:'0 4px' }}>
              ×
            </button>
          </div>
        )}
        {tab===0&&<TabErrorBoundary key="t0"><ItemDatabase items={items} vendors={vendors} projects={projects} assemblies={assemblies} proposals={proposals} onAdd={addItem} onUpdate={updateItem} onDelete={deleteItem} onDeleteDirect={deleteItemDirect} onAddAssembly={addAssembly} onUpdateAssembly={updateAssembly} onDeleteAssembly={deleteAssembly} saved={savedI} isOwner={isOwner} onAddVendor={addVendorInline} onNavigate={onNavigate} initialItemId={itemIdFromPath(activePath)} onFlushSave={flushSaves} onSaveDirect={saveItemDirect} /></TabErrorBoundary>}
        {tab===1&&<TabErrorBoundary key="t1"><TrackerView items={items} projects={projects} onUpdate={updateItem} onDelete={deleteItem} saved={savedI} isOwner={isOwner} onNavigate={onNavigate} /></TabErrorBoundary>}
        {tab===26&&<TabErrorBoundary key="t26"><CalendarView onNavigate={onNavigate} /></TabErrorBoundary>}
        {tab===27&&<TabErrorBoundary key="t27"><MessagesTab /></TabErrorBoundary>}
        {tab===2&&<TabErrorBoundary key="t2"><ProposalsAndPOs items={items} projects={projects} proposals={proposals} setProposals={setProposals} onUpdateItem={updateItem} vendors={vendors} /></TabErrorBoundary>}
        {tab===3&&<TabErrorBoundary key="t3"><DocumentsTab items={items} projects={projects} onUpdateItem={updateItem}
          vendors={vendors}
          onAddVendor={addVendorInline}
          onImportVendors={function(newVendors){
            setVendors(function(prev){
              var existingNames=new Set(prev.map(function(v){return v.name.toLowerCase();}));
              var toAdd=newVendors.filter(function(v){return!existingNames.has(v.name.toLowerCase());});
              return prev.concat(toAdd);
            });
          }}
          onAddItems={function(newItems){ addItems(newItems); }}
        /></TabErrorBoundary>}
        {tab===4&&<TabErrorBoundary key="t4"><VendorDirectory vendors={vendors} setVendors={setVendors} saved={savedV} firmSettings={firmSettings} /></TabErrorBoundary>}
        {tab===6&&<TabErrorBoundary key="t6"><FeatureRequests userEmail={userEmail} /></TabErrorBoundary>}
        {tab===7&&<TabErrorBoundary key="t7">{userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"?<ProjectDirectory projects={projects} setProjects={setProjects} saved={savedP} clients={clients} vendors={vendors} items={items} onUpdateItem={ctx.updateItem} />:null}</TabErrorBoundary>}
        {tab===8&&<TabErrorBoundary key="t8">{isFinanceNav?<BillingTab items={items} projects={projects} proposals={proposals} vendorPayments={vendorPayments} setVendorPayments={setVendorPayments} userEmail={userEmail} firmSettings={firmSettings} clients={clients} />:null}</TabErrorBoundary>}
        {tab===9&&<TabErrorBoundary key="t9"><div>
          {(function(){
            var last=localStorage.getItem("csid_last_export");
            var sevenDays=7*24*60*60*1000;
            var overdue=!last||(Date.now()-parseInt(last))>sevenDays;
            return (
              <div style={{background:overdue?"#fff8e1":"#f5f9ff",border:"1px solid "+(overdue?"#ffe082":"#c7d7f8"),borderRadius:10,padding:20,marginBottom:20}}>
                {overdue&&<div style={{background:"#ffe082",borderRadius:6,padding:"6px 12px",marginBottom:12,fontSize:12,fontWeight:600,color:"#5d4037"}}>⚠ Weekly CSV backup overdue — please download one now and save it to Dropbox.</div>}
                <div style={{fontWeight:700,fontSize:14,color:overdue?"#e65100":"#0277bd",marginBottom:6}}>📦 CSV Backup</div>
                <div style={{fontSize:12,color:"#555",marginBottom:12}}>Download a full backup of all items. Save it to Dropbox or Google Drive as an off-device copy.</div>
                <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                  <button
                    onClick={function(){
                      var csv=generateFullCSV(items);
                      var date=new Date().toISOString().slice(0,10);
                      downloadCSV(csv,"CimonStarr_ItemBackup_"+date+".csv");
                      localStorage.setItem("csid_last_export",String(Date.now()));
                      window._exportDue=false;
                    }}
                    style={{padding:"8px 20px",background:overdue?"#e65100":"#0277bd",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:600,cursor:"pointer"}}
                  >⬇ Download CSV Backup Now</button>
                  <div style={{fontSize:11,color:"#888"}}>
                    {last?"Last backup: "+new Date(parseInt(last)).toLocaleDateString()+" at "+new Date(parseInt(last)).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"No backup yet"}
                  </div>
                </div>
                <div style={{fontSize:11,color:"#aaa",marginTop:10}}>Backup includes: all items, specs, pricing, status, room, vendor, sidemark, tag, notes</div>
              </div>
            );
          })()}
          <div style={{background:"#f5f9f5",border:"1px solid #c8e6c9",borderRadius:10,padding:20,marginBottom:20}}>
            <div style={{fontWeight:700,fontSize:14,color:"#2e7d32",marginBottom:6}}>📦 Export Images for InDesign</div>
            <div style={{fontSize:12,color:"#555",marginBottom:12}}>Download all item images as an organized ZIP file, sorted by project and room. Ready to place in InDesign.</div>
            <ImageExportPanel items={items} projects={projects} />
          </div>
          {userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"&&<FullExportPanel />}
          {userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"&&<DataProtection
            items={items} vendors={vendors} projects={projects} assemblies={assemblies}
            onRestore={function(data){ restoreData(data); }}
          />}
        </div></TabErrorBoundary>}
        {tab===10&&<TabErrorBoundary key="t10">{userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"?<ClientDirectory clients={clients} setClients={setClients} saved={savedC} />:null}</TabErrorBoundary>}
        {tab===11&&<TabErrorBoundary key="t11">{userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"?<FirmSettings settings={firmSettings} setSettings={setFirmSettings} savePatch={ctx.saveFirmSettingsPatch} saved={savedFS} items={items} updateItem={updateItem} />:null}</TabErrorBoundary>}
        {tab===12&&<TabErrorBoundary key="t12"><div style={{padding:"40px",textAlign:"center",color:"#bbb"}}>Sample Requests — coming soon</div></TabErrorBoundary>}
        {tab===13&&<TabErrorBoundary key="t13"><QuotesTab items={items} vendors={vendors} projects={projects} onUpdateItem={updateItem} /></TabErrorBoundary>}
        {tab===14&&<TabErrorBoundary key="t14"><div style={{padding:"40px",textAlign:"center",color:"#bbb"}}>Memo Requests — coming soon</div></TabErrorBoundary>}
        {tab===15&&<TabErrorBoundary key="t15"><ProposalsTab2 /></TabErrorBoundary>}
        {tab===16&&<TabErrorBoundary key="t16"><PurchaseOrdersTab /></TabErrorBoundary>}
        {tab===17&&<TabErrorBoundary key="t17"><SchedulesTab items={items} projects={projects} vendors={vendors} firmSettings={firmSettings} assemblies={assemblies} /></TabErrorBoundary>}
        {tab===18&&<TabErrorBoundary key="t18"><ProjectNotesTab /></TabErrorBoundary>}
        {tab===19&&<TabErrorBoundary key="t19"><ReceiptsTab /></TabErrorBoundary>}
        {tab===20&&<TabErrorBoundary key="t20">{isFinanceNav?<InvoicesTab />:null}</TabErrorBoundary>}
        {tab===22&&<TabErrorBoundary key="t22">{isFinanceNav?<OwnerDrawsSection />:null}</TabErrorBoundary>}
        {tab===23&&<TabErrorBoundary key="t23">{userEmail&&userEmail.toLowerCase()==="c@cimonstarr.com"?<FirmCreditCardsSection />:null}</TabErrorBoundary>}
        {tab===24&&<TabErrorBoundary key="t24">{isFinanceNav?<ReconciliationSection />:null}</TabErrorBoundary>}
        {tab===25&&<TabErrorBoundary key="t25">{isFinanceNav?<Ledger firmSettings={firmSettings} items={items} updateItem={updateItem} />:null}</TabErrorBoundary>}
      </div>
    </div>
  );
}

export default App;

// ── ERROR BOUNDARY ────────────────────────────────────────────────────────────
// Catches any tab render crash and shows a helpful message instead of white screen
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Tab render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          margin: 24, padding: 24, background: '#fff5f5',
          border: '2px solid #ffcdd2', borderRadius: 10,
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#c62828', marginBottom: 8 }}>⚠ Something went wrong on this tab</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>Your data is safe — this is a display error only. Try refreshing, or switch to another tab and back.</div>
          <div style={{
            background: '#fff', border: '1px solid #ffcdd2', borderRadius: 6,
            padding: 12, fontSize: 11, fontFamily: 'monospace',
            color: '#c62828', wordBreak: 'break-all', maxHeight: 200, overflowY: 'auto'
          }}>{String(this.state.error)}</div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              marginTop: 16, padding: '8px 20px', background: '#1a1a1a', color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer'
            }}
          >Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Re-export App wrapped in error boundary
const AppWithBoundary = function(props) {
  return <TabErrorBoundary><App {...props} /></TabErrorBoundary>;
};
export { AppWithBoundary };

